import { filterLayoutBlocks } from './proposalCraftUtils';

const STORAGE_PREFIX = 'idotoo_proposal_preview_';

/**
 * Builds the payload the supplier preview page reads from localStorage (current editor + form state).
 * @param {object} options
 * @param {'agreement' | 'template'} options.kind
 * @param {object} options.formData
 * @param {import('react').MutableRefObject} options.craftEditorRef
 * @param {string} [options.businessName]
 */
export function buildProposalPreviewPayload({ kind, formData, craftEditorRef, businessName }) {
  const blocksFromEditor = craftEditorRef?.current?.getBlocks?.();
  const raw = Array.isArray(blocksFromEditor) ? blocksFromEditor : (formData.content?.blocks || []);
  const blocks = filterLayoutBlocks(raw);
  const lineItems = (formData.lineItems || []).map((i, idx) => ({
    _id: `pv_${kind}_${idx}`,
    description: (i.description || '').trim() || '—',
    quantity: Number(i.quantity) || 1,
    unitPrice: parseFloat(i.unitPrice) || 0,
    taxPercentage: parseFloat(i.taxPercentage) || 0,
    priceIncludesTax: Boolean(i.priceIncludesTax),
    isOptional: !!i.isOptional
  }));
  return {
    kind,
    businessName: businessName || 'Your business',
    message: kind === 'agreement' ? (formData.message || '').trim() : '',
    termsText: (formData.termsText || '').trim(),
    currency: formData.currency || 'GBP',
    content: { blocks },
    lineItems,
    additionalInformationFields: formData.additionalInformationFields || []
  };
}

/**
 * Stores payload under a random key and opens the supplier preview route in a new tab.
 * @param {ReturnType<typeof buildProposalPreviewPayload>} payload
 */
export function openProposalSupplierPreview(payload) {
  const key = `${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
  const storageKey = `${STORAGE_PREFIX}${key}`;
  try {
    localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    throw new Error('Could not open preview. Check that storage is available (private browsing can block this).');
  }
  const url = `${window.location.origin}/supplier/agreements/preview?k=${encodeURIComponent(key)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function readProposalPreviewFromStorage(key) {
  if (!key) return null;
  const raw = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
